// src/components/games/diceMatch/DiceMatchUpgrades.tsx

import React, { useState, useEffect } from 'react';
import {
  IonPage,
  IonHeader,
  IonToolbar,
  IonTitle,
  IonContent,
  IonButton,
  IonIcon,
  IonCard,
  IonCardHeader,
  IonCardTitle,
  IonCardContent,
  IonBadge,
  IonBackButton,
  IonButtons,
  IonAlert,
  IonProgressBar,
} from '@ionic/react';
import { star, checkmarkCircle } from 'ionicons/icons';
import {
  getMetaProgression,
  purchaseMetaUpgrade,
  MetaProgression,
} from '../../../services/diceMatchService';
import './DiceMatchUpgrades.css';

interface UpgradeOption {
  id: 'starting_dice' | 'starting_grid' | 'currency_multiplier';
  name: string;
  description: string;
  icon: string;
  baseCost: number;
  maxLevel: number;
  benefit: (level: number) => string;
}

const UPGRADE_OPTIONS: UpgradeOption[] = [
  {
    id: 'starting_dice',
    name: 'Extra Starting Dice',
    description: 'Start with more dice in reserve',
    icon: '🎲',
    baseCost: 25,
    maxLevel: 10,
    benefit: (level) => `+${level} dice (${3 + level} total)`,
  },
  {
    id: 'starting_grid',
    name: 'Larger Grid',
    description: 'Expand your playing field',
    icon: '📐',
    baseCost: 35,
    maxLevel: 5,
    benefit: (level) => {
      const size = 4 + (level > 0 ? 1 : 0);
      return `${size}x${size} grid`;
    },
  },
  {
    id: 'currency_multiplier',
    name: 'Currency Bonus',
    description: 'Earn more currency from games',
    icon: '💰',
    baseCost: 20,
    maxLevel: 10,
    benefit: (level) => `+${level * 10}% currency earned`,
  },
];

const DiceMatchUpgrades: React.FC = () => {
  const [meta, setMeta] = useState<MetaProgression | null>(null);
  const [loading, setLoading] = useState(true);
  const [purchasing, setPurchasing] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [selectedUpgrade, setSelectedUpgrade] = useState<UpgradeOption | null>(null);
  const [showSuccess, setShowSuccess] = useState(false);
  const [showError, setShowError] = useState(false);

  useEffect(() => {
    loadMeta();
  }, []);

  const loadMeta = async () => {
    setLoading(true);
    const metaData = await getMetaProgression();
    setMeta(metaData);
    setLoading(false);
  };

  const calculateCost = (upgrade: UpgradeOption, currentLevel: number): number => {
    // Cost increases with each level: baseCost * (1 + level)
    return upgrade.baseCost * (1 + currentLevel);
  };

  const handlePurchaseClick = (upgrade: UpgradeOption) => {
    setSelectedUpgrade(upgrade);
    setShowConfirm(true);
  };

  const confirmPurchase = async () => {
    if (!selectedUpgrade || !meta) return;

    const currentLevel = (meta as any)[`upgrade_${selectedUpgrade.id}`];
    const cost = calculateCost(selectedUpgrade, currentLevel);

    if (meta.meta_currency < cost) {
      setShowError(true);
      setShowConfirm(false);
      return;
    }

    setPurchasing(true);
    const success = await purchaseMetaUpgrade(selectedUpgrade.id, cost);
    setPurchasing(false);
    setShowConfirm(false);

    if (success) {
      setShowSuccess(true);
      await loadMeta(); // Reload to show updated values
    } else {
      setShowError(true);
    }
  };

  if (loading || !meta) {
    return (
      <IonPage>
        <IonHeader>
          <IonToolbar>
            <IonButtons slot="start">
              <IonBackButton defaultHref="/games/dice-match/menu" />
            </IonButtons>
            <IonTitle>Upgrades</IonTitle>
          </IonToolbar>
        </IonHeader>
        <IonContent>
          <div style={{ padding: '20px', textAlign: 'center' }}>
            <IonProgressBar type="indeterminate" />
            <p>Loading upgrades...</p>
          </div>
        </IonContent>
      </IonPage>
    );
  }

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar>
          <IonButtons slot="start">
            <IonBackButton defaultHref="/games/dice-match/menu" />
          </IonButtons>
          <IonTitle>Permanent Upgrades</IonTitle>
        </IonToolbar>
      </IonHeader>

      <IonContent className="upgrades-content">
        <div className="upgrades-container">
          {/* Currency Display */}
          <IonCard className="currency-header">
            <IonCardContent>
              <div className="currency-display">
                <span className="currency-icon">⭐</span>
                <span className="currency-amount">{meta.meta_currency}</span>
                <span className="currency-label">Meta Currency</span>
              </div>
            </IonCardContent>
          </IonCard>

          {/* Upgrade Cards */}
          {UPGRADE_OPTIONS.map((upgrade) => {
            const currentLevel = (meta as any)[`upgrade_${upgrade.id}`];
            const cost = calculateCost(upgrade, currentLevel);
            const canAfford = meta.meta_currency >= cost;
            const isMaxLevel = currentLevel >= upgrade.maxLevel;

            return (
              <IonCard key={upgrade.id} className="upgrade-card">
                <IonCardHeader>
                  <div className="upgrade-header">
                    <div className="upgrade-icon">{upgrade.icon}</div>
                    <div className="upgrade-title-section">
                      <IonCardTitle>{upgrade.name}</IonCardTitle>
                      <p className="upgrade-description">{upgrade.description}</p>
                    </div>
                    <IonBadge color={isMaxLevel ? 'success' : 'primary'}>
                      Level {currentLevel}/{upgrade.maxLevel}
                    </IonBadge>
                  </div>
                </IonCardHeader>
                <IonCardContent>
                  <div className="upgrade-benefit">
                    <strong>Current Bonus:</strong> {upgrade.benefit(currentLevel)}
                  </div>

                  {!isMaxLevel && (
                    <>
                      <div className="upgrade-next">
                        <strong>Next Level:</strong> {upgrade.benefit(currentLevel + 1)}
                      </div>
                      <IonButton
                        expand="block"
                        color={canAfford ? 'primary' : 'medium'}
                        disabled={!canAfford || purchasing}
                        onClick={() => handlePurchaseClick(upgrade)}
                        style={{ marginTop: '12px' }}
                      >
                        <IonIcon icon={star} slot="start" />
                        {canAfford
                          ? `Upgrade for ⭐ ${cost}`
                          : `Need ⭐ ${cost} (${cost - meta.meta_currency} more)`}
                      </IonButton>
                    </>
                  )}

                  {isMaxLevel && (
                    <div className="max-level-badge">
                      <IonIcon icon={checkmarkCircle} /> Max Level Reached!
                    </div>
                  )}
                </IonCardContent>
              </IonCard>
            );
          })}

          {/* Info Card */}
          <IonCard className="info-card">
            <IonCardContent>
              <h4>💡 About Permanent Upgrades</h4>
              <ul>
                <li>Upgrades apply to ALL future games</li>
                <li>Costs increase with each level purchased</li>
                <li>Earn meta currency by playing and scoring points</li>
                <li>Each sparkle created gives +10 bonus currency!</li>
                <li>Strategic upgrades can dramatically improve your scores</li>
              </ul>
            </IonCardContent>
          </IonCard>
        </div>

        {/* Purchase Confirmation Alert */}
        <IonAlert
          isOpen={showConfirm}
          onDidDismiss={() => setShowConfirm(false)}
          header="Confirm Purchase"
          message={
            selectedUpgrade
              ? `Purchase ${selectedUpgrade.name} Level ${
                  ((meta as any)[`upgrade_${selectedUpgrade.id}`] || 0) + 1
                } for ⭐ ${calculateCost(
                  selectedUpgrade,
                  (meta as any)[`upgrade_${selectedUpgrade.id}`] || 0
                )}?`
              : ''
          }
          buttons={[
            {
              text: 'Cancel',
              role: 'cancel',
            },
            {
              text: 'Purchase',
              handler: confirmPurchase,
            },
          ]}
        />

        {/* Success Alert */}
        <IonAlert
          isOpen={showSuccess}
          onDidDismiss={() => setShowSuccess(false)}
          header="✅ Upgrade Purchased!"
          message="Your permanent upgrade has been applied. It will be active in your next game!"
          buttons={['OK']}
        />

        {/* Error Alert */}
        <IonAlert
          isOpen={showError}
          onDidDismiss={() => setShowError(false)}
          header="❌ Purchase Failed"
          message="Unable to complete purchase. Please try again."
          buttons={['OK']}
        />
      </IonContent>
    </IonPage>
  );
};

export default DiceMatchUpgrades;
